import {DrawerBody, DrawerHeader} from '@cypro/ui/components'
import {numberFormatter, getShortDateFormatter} from '@cypro/ui/utils'
import {Drawer, Progress, Table} from '@chakra-ui/react'
import {Avatar, Box, Flex, Text, HStack, Heading} from '@chakra-ui/react'
import {DrugDetails} from '@cypro/engine/utils'
import {useTranslate} from '@tolgee/react'
import {Decimal} from 'decimal.js'
import {LuFlaskConical} from 'react-icons/lu'

interface AdministrationHistoryDrawerProps {
  dose: DrugDetails
}

export function AdministrationHistoryDrawer({dose}: AdministrationHistoryDrawerProps) {
  const {t} = useTranslate('common')
  const lifetimeLimit = dose.drugLimits?.lifetime ? new Decimal(dose.drugLimits?.lifetime) : undefined
  const amountToToday = dose.totalAmount ? new Decimal(dose.totalAmount) : undefined
  const today = new Date().getTime()
  const percentage = lifetimeLimit && amountToToday ? (100 * amountToToday.toNumber()) / lifetimeLimit.toNumber() : 0
  return (
    <>
      <DrawerHeader>
        <Drawer.Title>{t('administration_history')}</Drawer.Title>
      </DrawerHeader>

      <DrawerBody height="100%" flex="1 1 0%" spaceY="5">
        <Flex paddingY="4" alignItems="flex-start" justifyContent="space-between" width="100%">
          <HStack gapX="3">
            <Avatar.Root size="xl" shape="square" rounded="md">
              <LuFlaskConical size="1.5rem" />
            </Avatar.Root>
            <Box>
              <Heading as="h3" fontSize="lg" fontWeight="medium" fontFamily="body">
                {dose.drugName || 'Unknown substance'}
              </Heading>
            </Box>
          </HStack>
          <Progress.Root
            size={'sm'}
            shape={'rounded'}
            colorPalette={percentage > 50 ? 'orange' : 'green'}
            borderRadius={'full'}
            value={percentage > 100 ? 100 : percentage}
            max={100}
            width={'100%'}
            maxWidth={'150px'}
          >
            <Progress.Label mb="2">{t('percentage_reached', {percentage: percentage.toFixed(2)})}</Progress.Label>
            <Progress.Track borderRadius={'full'}>
              <Progress.Range />
            </Progress.Track>
          </Progress.Root>
        </Flex>
        <Text fontWeight="medium" fontSize="md">
          {t('records')}
        </Text>
        <Table.Root>
          <Table.Header>
            <Table.Row borderBottom="2px solid" borderColor="gray.muted">
              <Table.ColumnHeader bg="white">{t('date')}</Table.ColumnHeader>
              <Table.ColumnHeader bg="white" textAlign="end">
                {t('administration_history.dose', {doseUnit: dose.doseUnit || '-'})}
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {Object.entries(dose?.history || {})
              .filter(([datekey, _history]) => Number(datekey) < today)
              .map(([dateKey, history]) => {
                return (
                  <Table.Row key={dateKey}>
                    <Table.Cell border="none">
                      <Text fontSize={'sm'} fontWeight={'normal'}>
                        {getShortDateFormatter().format(Number(dateKey))}
                      </Text>
                    </Table.Cell>
                    <Table.Cell border="none " textAlign="end">
                      <Text fontSize={'sm'} fontWeight={'normal'}>
                        {history.amountInDay > 0 ? numberFormatter.format(history.amountInDay) : '-'}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                )
              })}
          </Table.Body>
        </Table.Root>
      </DrawerBody>
    </>
  )
}
